import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import breakAtSeparators from '../../../../utils/break-at-separators';
type RuleTraceRowProps = {
  /** Group/branch name shown before the conditions (e.g. "Risk Level"). */
  name?: string;
  /** Whether any rule in this group fired — drives the purple highlight. */
  triggered: boolean;
  /** Triggered condition expressions, in order. Empty when nothing triggered. */
  conditions: string[];
  /** Label shown when nothing triggered (e.g. `Risk level: no rules triggered`). */
  noTriggerLabel: string;
  /** Content rendered after the "then" connector (the assigned value / branch outcome). */
  outcome: ReactNode;
  /** First entry in the trace — the rail starts at this row's dot. */
  isFirstEntry: boolean;
  /** Last entry in the trace — the rail ends at this row's dot. */
  isLastEntry: boolean;
};
const RAIL = 'w-px flex-1 bg-[var(--border-color-tertiary)]';

/**
 * A single rule/conditional evaluation row in the case review Rules tab.
 */
const RuleTraceRow = t0 => {
  const $ = _c(37);
  const {
    name,
    triggered,
    conditions,
    noTriggerLabel,
    outcome,
    isFirstEntry,
    isLastEntry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.onboardingTrace"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const hasRail = !(isFirstEntry && isLastEntry);
  let t2;
  if ($[1] !== hasRail || $[2] !== isFirstEntry || $[3] !== isLastEntry || $[4] !== triggered) {
    t2 = hasRail && <div className="flex w-1.5 shrink-0 flex-col items-center"><span className={cx("h-2.5 w-px shrink-0 bg-[var(--border-color-tertiary)]", isFirstEntry && "invisible")} /><span className={cx("size-1.5 shrink-0 rounded-full", triggered ? "bg-accent" : "bg-gray-300")} /><span className={cx(RAIL, isLastEntry && "invisible")} /></div>;
    $[1] = hasRail;
    $[2] = isFirstEntry;
    $[3] = isLastEntry;
    $[4] = triggered;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = triggered ? "bg-active border-secondary-hover text-secondary" : "bg-secondary border-primary text-quaternary";
  let t4;
  if ($[6] !== t3) {
    t4 = cx("inline-flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1 border rounded-sm text-snippet-3 wrap-anywhere md:wrap-normal", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== conditions || $[9] !== name || $[10] !== noTriggerLabel || $[11] !== t) {
    t5 = conditions.length > 0 ? <>{name && <span className="text-secondary">{name}:</span>}{conditions.map((expression, index) => <Condition key={expression} connector={index === 0 ? t(_temp) : t(_temp2)} expression={expression} />)}</> : <span>{noTriggerLabel}</span>;
    $[8] = conditions;
    $[9] = name;
    $[10] = noTriggerLabel;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t6) {
    t7 = <div className="flex gap-x-2.5">{t2}{t6}</div>;
    $[16] = t2;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== hasRail || $[20] !== isLastEntry) {
    t8 = hasRail && <div className="flex w-1.5 shrink-0 flex-col items-center"><span className={cx(RAIL, isLastEntry && "invisible")} /></div>;
    $[19] = hasRail;
    $[20] = isLastEntry;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = isLastEntry ? "pb-0" : "pb-3";
  const t10 = triggered ? "text-secondary" : "text-quaternary";
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = cx("pt-1.5 pl-4 text-snippet-3 before:mr-1 before:text-gray-300 before:content-['\u21B3']", t9, t10);
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t(_temp3);
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== outcome || $[28] !== t11 || $[29] !== t12) {
    t13 = <div className={t11}>{t12} {outcome}</div>;
    $[27] = outcome;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t8) {
    t14 = <div className="flex gap-x-2.5">{t8}{t13}</div>;
    $[31] = t13;
    $[32] = t8;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14 || $[35] !== t7) {
    t15 = <div className="flex flex-col">{t7}{t14}</div>;
    $[34] = t14;
    $[35] = t7;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
};
const Condition = t0 => {
  const $ = _c(9);
  const {
    connector,
    expression
  } = t0;
  let t1;
  if ($[0] !== connector) {
    t1 = <span className="text-tertiary">{connector}</span>;
    $[0] = connector;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== expression) {
    t2 = breakAtSeparators(expression);
    $[2] = expression;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-primary font-medium">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export default RuleTraceRow;
function _temp($) {
  return $.connectors.if;
}
function _temp2($_0) {
  return $_0.connectors.or;
}
function _temp3($_1) {
  return $_1.connectors.then;
}